import { NgClass, NgIf } from "@angular/common";
import {
    AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output,
    SimpleChanges, ViewChild, ViewEncapsulation, forwardRef
} from "@angular/core";
import { Router } from "@angular/router";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { CheckedState, TreeItem, TreeItemLookup, TreeViewComponent, TreeViewModule } from "@progress/kendo-angular-treeview";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";

import { ControlResizerOptions, ControlResizerService } from "@mysrc/services/framework";

import {
    AuthPermIdentifier, RequestDelete, RequestUserPermission, RequestUserPermissionSet,
    TreeDataItem, TreeDataItemCheckHighlighted, TreeInfoAuth
} from "@myapp/modules/fwcore/models";
import {
    AuthorizationService, ConfirmationDialogService, NotificationDialogService,
    NotificationPopupService
} from "@myapp/modules/fwcore/services";

import { AuthPerm } from "@myapp/features/admin/models";
import {
    RequestPermission, RequestPermissionSet, RequestPermissionTree,
    RequestRolePerm, RequestRolePermSet
} from "@myapp/features/admin/models/messages";
import { AdminService } from '@myapp/features/admin/services';
import { AuthTreeEditorComponent } from "../..";


@Component({
  selector: 'app-auth-tree',
  templateUrl: './auth-tree.component.html',
  styleUrls: ['./auth-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [ButtonModule, NgIf, TreeViewModule, NgClass
    , forwardRef(() => AuthTreeEditorComponent)]
})

export class AuthTreeComponent implements AfterViewInit, OnChanges, OnDestroy {

  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() public moduleIds: string[] | undefined | null = null;
  @Input() public licenseId: string | null = null;
  @Input() public hierarchyId: string | null = null;
  @Input() public roleId: string | null | undefined = null;
  @Input() public userFilterId: string | null | undefined = null;
  @Input() public authTypes: string[] | null = null;
  @Input() public authTypesExclude: string[] | null = null;
  @Input() public active: boolean | null = null;
  @Input() public cascadeLink: boolean | null = null;
  @Input() public includeMembers: boolean | null = null;
  @Input() public includeHierarchies: boolean | null = null;
  @Input() public includeLabels: boolean | null = null;
  @Input() public processFilter: number | null = null;
  @Input() public triggerSave: boolean | null = null;

  @Input() public showToolbar: boolean = false;

  @Input() public showSaveButton: boolean = false;
  @Input() public enableSaveButton: boolean = false;

  @Input() public showCreateButton: boolean = false;
  @Input() public showEditButton: boolean = false;
  @Input() public showReadOnlyButton: boolean = false;
  @Input() public showReorderButton: boolean = false;
  @Input() public showDeleteButton: boolean = false;


  @Input() public minHeight: number = 200;
  @Input() public widthThreshold: number = 600;

  @Input()
  set checkedIds(ids: string[]) {
    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }
  get checkedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  @Input()
  set highlightedIds(ids: string[]) {
    this.dataItems.forEach((item) => {
      item.highlighted = ids.includes(item.id!);
    });
  }
  get highlightedIds(): string[] {

    const highlightedIds: string[] = this.dataItems
      .filter((item) => item.highlighted)
      .map((item) => item.id!);

    return highlightedIds ?? [];
  }

  @Output() permissionsSavedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() selected: EventEmitter<TreeDataItem | null> = new EventEmitter();
  @Output() dataLoaded: EventEmitter<number | null> = new EventEmitter();

  /*-------------------------- public properties / methods --------------------*/

  public selectedItem!: TreeDataItem | null;
  public selectedKeys: string[] = [];
  public checkedItems: TreeDataItem[] = [];
  public checkedKeys: string[] = [];
  public editDataItem!: AuthPerm | null;
  public key = "id";
  public allParentNodes: string[] = [];
  public expandedKeys: string[] = this.allParentNodes.slice();

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    let boundClientRect = this.elem.nativeElement.getBoundingClientRect()
    ctrlOptions.elemTop = boundClientRect.top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    let h = 0;

    // check if toolbar displayed
    if (this.showToolbar || this.showSaveButton) {
      h = this.elem.nativeElement?.querySelector('.isg-authtree-toolbar')?.getBoundingClientRect().height;
      if (h)
        ctrlOptions.offsetTopHeight += h + 8;
      else
        ctrlOptions.offsetTopHeight += 38;
    }

    // check if info message is being displayed
    if (this.messageInfo != null && this.messageInfo.length > 0) {

      h = this.elem.nativeElement?.querySelector('.isg-authtree-message')?.getBoundingClientRect().height;
      if (h)
        ctrlOptions.offsetTopHeight += h;
      else
        ctrlOptions.offsetTopHeight += 22;
    }

    if (window.innerWidth > this.widthThreshold)
      this.heightTree = this.controlResizer.calculateHeight(ctrlOptions);
    else {
      // Do not dynamically resize height if below the width threshold
      this.heightTree = this.minHeight;
    }

  }

  public setCheckedSelection(items: TreeInfoAuth[]) {

    this.treeInfos = items;

    let ids: string[] = [];
    let excludedIds: string[] = [];
    let hasChildExcludedIds: string[] = [];
    let customIds: string[] = [];
    let hasChildCustomIds: string[] = [];

    if (items && items.length > 0) {
      ids = items.filter(item => !item.excluded).map(item => item.id!);

      excludedIds = items.filter(item => item.excluded).map(item => item.id!);
      hasChildExcludedIds = items.filter(item => (item.hasChildExcludedIds === true)).map(item => item.id!);

      customIds = items.filter(item => item.source == "User" && item.excluded !== true).map(item => item.id!);
      hasChildCustomIds = items.filter(item => (item.hasChildCustomIds === true)).map(item => item.id!);
    }

    this.checkedIds = ids;

    this.excludedIds = excludedIds;
    this.hasChildExcludedIds = hasChildExcludedIds;

    this.customIds = customIds;
    this.hasChildCustomIds = hasChildCustomIds;

    // refresh tree
    this.checkedKeysCopy = ids;
    this.checkedKeys = [...this.checkedKeysCopy];
  }

  public messageInfo: string | null = null;

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(TreeViewComponent, { static: false })
  protected treeViewComp!: TreeViewComponent;

  protected destroy$ = new Subject();
  protected heightTree: number = 200;
  protected loading: boolean = true;

  protected dataItems: TreeDataItemCheckHighlighted[] = [];
  protected isViewOnlyEditor: boolean = false;
  protected reqPerm: RequestUserPermission = {

  }
  protected checkedDataItems: AuthPermIdentifier[] = [];
  protected treeInfos: TreeInfoAuth[] = [];

  protected excludedIds: string[] = [];
  protected hasChildExcludedIds: string[] = [];
  protected customIds: string[] = [];
  protected hasChildCustomIds: string[] = [];

  protected checkedKeysCopy: string[] = [];

  /*-------------------------- Constructor  --------------------------*/

  constructor(
    protected authService: AuthorizationService
    , protected router: Router
    , protected adminService: AdminService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected notification: NotificationPopupService
    , protected controlResizer: ControlResizerService
    , protected elem: ElementRef

  ) {
  }

  /*----------------------------------- ng  --------------------------*/

  ngAfterViewInit(): void {

    this.reqPerm.route = this.router.url;
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.moduleIds && !changes.moduleIds.firstChange) {
      this.getPermissionTrees();
    }
    else if (changes.roleId && !changes.roleId.firstChange) {
      this.getRolePermission()
    }
    else if (changes.isSavePerm
      && !changes.isSavePerm.firstChange
      && changes.isSavePerm.currentValue) {

      if (this.roleId && this.roleId.length > 0)
        this.saveRolePermissions();
      else if (this.userFilterId && this.userFilterId.length > 0)
        this.saveUserPermissions();
    }
  }

  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  protected onAddClick() {

    this.isViewOnlyEditor = false;
    this.getPermissionDetail(true); //sets true for add to get heraricyID from API

  }

  protected onEditClick() {

    this.isViewOnlyEditor = false;
    this.getPermissionDetail();
  }

  protected onDeleteClick() {

    this.dialogConfirm.open('Confirm Deletion', 'Are you sure you want to delete this permission?')
      .subscribe((result: any) => {
        if (result.value && this.selectedItem?.id) {
          this.deletePermission(this.selectedItem.id);
        }
      });
  }

  protected onViewClick() {

    this.isViewOnlyEditor = true;
    this.getPermissionDetail();
  }

  protected onToggle() {

    this.ToggleNodes(this.selectedItem);
  }

  protected onSaveClick() {

    if (this.userFilterId && this.userFilterId.length > 0)
      this.saveUserPermissions();
    else if (this.roleId && this.roleId.length > 0)
      this.saveRolePermissions();

  }

  protected onMoveItem(direction: 'up' | 'down'): void {

    if (!this.selectedItem || !this.selectedItem.id) {
      return; // No selected item, do nothing
    }

    const itemId = this.selectedItem.id;
    this.dataItems = this.moveItemWithinSiblings(this.dataItems, itemId, direction);
  }

  protected onCancelEditor(): void {

    this.editDataItem = null;
  }

  protected onSaveEditor(permission: AuthPerm): void {

    const request: RequestPermissionSet = {
      data: [permission]
    };

    if (permission.id) {
      this.updatePermission(request);
    } else {
      this.createPermission(request);
    }

    this.resetSelectedItems();
  }

  protected onSelectionChange(event: TreeItem): void {

    if (this.selectedItem?.id === event.dataItem?.id) {
      this.selectedItem = null;
      this.selectedKeys = [];

      document.activeElement?.querySelector('.k-focus')?.classList.remove('k-focus');

    }
    else {
      this.selectedItem = event.dataItem;
    }
  }

  protected onItemChecking(itemLookup: TreeItemLookup): void {

    // Do not allow any changes
    if (this.showSaveButton !== true)
      this.checkedKeys = [...this.checkedKeysCopy];
  }

  /*--------------------------- internal functions  --------------------------*/

  protected getPermissionTrees(): void {

    this.loading = true;
    this.checkedKeys.length = 0;

    const req: RequestPermissionTree = {
      licenseId: this.licenseId
      , moduleIds: this.moduleIds
      , hierarchyId: this.hierarchyId
      , authTypes: this.authTypes
      , authTypesExclude: this.authTypesExclude
      , includeMembers: this.includeMembers
      , includeHierarchies: this.includeHierarchies
      , includeLabels: this.includeLabels
      , cascadeLink: this.cascadeLink
      , active: this.active
      , processFilter: this.processFilter
    };

    this.authService.permissionTreeGet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => this.loading = false))
      .subscribe(
        resp => {

          this.setCssClass(resp.data);
          this.dataItems = resp.data!;
          this.dataLoaded.emit(this.dataItems?.length);

          if (resp.data && resp.data.length > 0) {
            this.getAllParentTextProperties(resp.data);
          }
        }
      );
  }

  protected getRolePermission() {

    this.loading = true;
    this.checkedKeys.length = 0;

    const req: RequestRolePerm = {
      moduleId: this.moduleIds?.join()
      , licenseId: this.licenseId
      , roleId: this.roleId
    }

    this.authService.rolePermissionGet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => this.loading = false))
      .subscribe((res) => {

        if (res.data) {

          this.checkedKeysCopy = res.data.reduce((keys: string[], item) => {
            if (!!item.id) {
              keys.push(item.id);
            }
            return keys;
          }, []);

          this.checkedKeys = [...this.checkedKeysCopy];
        }

      });

  }

  protected getPermissionDetail(isAddMode: boolean = false) {

    if (this.selectedItem?.id) {
      const req: RequestPermission = {
        id: this.selectedItem.id
        , moduleId: this.moduleIds?.join()
      };

      this.authService.permissionGet$(req)
        .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
        .subscribe(
          resp => {
            if (resp.data && resp.data.length > 0) {
              const permissionDetail = resp.data[0];
              this.handlePermissionDetails(isAddMode, permissionDetail);
            }
          }
        );
    }
  }

  protected handlePermissionDetails(isAddMode: boolean, permissionDetail: AuthPerm) {

    if (isAddMode) {
      this.editDataItem = new AuthPerm();
      this.editDataItem.parentId = permissionDetail?.id;
      this.editDataItem.hierarchyId = permissionDetail.hierarchyId;
    } else {
      this.editDataItem = permissionDetail;
    }
  }

  protected saveRolePermissions() {

    // reset the list
    this.checkedDataItems.length = 0;
    let assignments = new Map<string, AuthPermIdentifier>();

    this.getPermissionAssignments(this.dataItems, this.checkedKeys, assignments);

    if (this.roleId) {

      this.loading = true;

      const req: RequestRolePermSet = {
        licenseId: this.licenseId
        , moduleId: this.moduleIds?.join()
        , roleId: this.roleId
        , hierarchyId: this.hierarchyId
        , authTypes: this.authTypes
        , data: [...assignments.values()]
      };

      this.authService.rolePermissionSet$(req)
        .pipe(takeUntil(this.destroy$)
          , finalize(() => {
            this.loading = false;
            this.permissionsSavedChange.emit(false);
            assignments.clear();
          }))
        .subscribe((res) => {
          if (res.success) {
            this.dialogNotification.openNotificationDialog('Success', 'Permissions saved successfully');
          }
          else {
            this.dialogNotification.openNotificationDialog('Error', 'Something went wrong');
          }
        });

    }
  }

  protected saveUserPermissions() {

    // reset the list
    this.checkedDataItems.length = 0;
    let assignments = new Map<string, AuthPermIdentifier>();

    this.getPermissionAssignments(this.dataItems, this.checkedKeys, assignments);

    if (this.userFilterId) {

      this.loading = true;

      const req: RequestUserPermissionSet = {
        userIds: this.userFilterId ? [this.userFilterId] : null
        , licenseId: this.licenseId
        , moduleId: this.moduleIds?.join()
        , updateRoles: false
        , updatePerms: true
        , roleIds: null
        , hierarchyIds: this.hierarchyId ? [this.hierarchyId] : null
        , authTypes: this.authTypes
        , data: [...assignments.values()]
      };

      this.authService.userPermissionSet$(req)
        .pipe(takeUntil(this.destroy$)
          , finalize(() => {
            this.loading = false;
            assignments.clear();

            this.permissionsSavedChange.emit(false);

          }))
        .subscribe((res) => {
          if (res.success) {
            this.dialogNotification.openNotificationDialog('Success', 'Permissions saved successfully');
          }
          else {
            this.dialogNotification.openNotificationDialog('Error', 'Something went wrong');
          }
        });

    }
  }

  protected getPermissionAssignments(items: TreeDataItem[]
    , checkedKeys: string[]
    , result: Map<string
      , AuthPermIdentifier>): boolean {

    let added: boolean = false;

    for (const item of items) {

      if (item.id && checkedKeys.includes(item.id)) {

        result.set(item.id, { id: item.id, code: item.dataCode });

        added = true;
      }

      if (item.items && item.items.length > 0) {
        let childAdded = this.getPermissionAssignments(item.items, checkedKeys, result);

        if (childAdded == true) {
          result.set(item.id!, { id: item.id, code: item.dataCode });
          added = true;
        }
      }
    }

    return added;
  }

  protected createPermission(request: RequestPermissionSet): void {
    this.authService.permissionSet$(request).subscribe(
      (response) => {
        if (response.success) {
          this.notification.showNotification('permission added succesfully', 'success');
          this.refreshPermissions();
        }
      }
    );
  }

  protected updatePermission(request: RequestPermissionSet): void {
    this.authService.permissionUpdate$(request).subscribe(
      (response) => {
        if (response.success) {
          this.notification.showNotification('permission updated succesfully', 'success');

          this.refreshPermissions();
        }
      }
    );
  }

  protected deletePermission(permissionId: string): void {
    const req: RequestDelete = {
      id: permissionId
      , moduleId: this.moduleIds?.join()
    };

    this.authService.permissionDelete$(req).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success) {
          this.notification.showNotification('permission deleted succesfully', 'success');
          this.refreshPermissions();

        }

      });
  }

  protected refreshPermissions(): void {
    this.getPermissionTrees();
  }

  protected resetSelectedItems(): void {
    this.selectedItem = null;
    this.editDataItem = null;
  }

  protected getAllParentTextProperties(items: TreeDataItem[]) {

    items.forEach(i => {
      if (i.text && i.items) {
        this.allParentNodes.push(i.text);
        this.getAllParentTextProperties(i.items);
      }
    });
  }

  protected ToggleNodes(selectedItem: TreeDataItem | null) {
    if (!selectedItem) {
      this.expandedKeys = this.expandedKeys.length === 0 ? this.allParentNodes.slice() : [];
      return;
    }

    if (this.isItemSelected(selectedItem)) {
      this.collapseChildren(selectedItem);
    } else {
      this.expandChildren(selectedItem);
    }
  }

  protected expandChildren(item: TreeDataItem) {
    if (item.items && item.text) {
      this.expandedKeys = [...this.expandedKeys, item.text];
      item.items.forEach(child => this.expandChildren(child));
    }
  }

  protected collapseChildren(item: TreeDataItem) {
    if (item.items) {
      this.expandedKeys = this.expandedKeys.filter(key => key !== item.text);
      item.items.forEach(child => this.collapseChildren(child));
    }
  }

  protected isItemSelected(item: TreeDataItem): boolean {
    if (item.text) {
      return this.expandedKeys.includes(item.text);
    }
    return false;
  }

  //---------------- Re ordering -----------//

  protected moveItemWithinSiblings(data: TreeDataItem[], itemId: string, direction: 'up' | 'down'): TreeDataItem[] {

    const index = this.findIndexInTree(data, itemId);

    if (index === -1) {
      return data; // Item not found, return unchanged data
    }

    const itemToMove = this.findItemInTree(data, itemId);
    if (!itemToMove) {
      return data; // Item not found, return unchanged data
    }

    // Find the parent of the item
    const parent = this.findParent(data, itemId);

    if (!parent || !parent.items) {
      return data; // Parent not found or has no items
    }

    const siblings = parent.items;

    if (siblings.length <= 1) {
      return data; // No siblings or only one sibling, no change needed
    }

    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < siblings.length) {
      this.moveItemAmongSiblings(siblings, index, targetIndex, direction);
    }

    return data;
  }

  protected moveItemAmongSiblings(siblings: TreeDataItem[], index: number, newIndex: number, direction: 'up' | 'down'): void {

    if (newIndex < 0 || newIndex >= siblings.length) {
      return; // Invalid newIndex
    }

    this.loading = true;

    const [movedItem] = siblings.splice(index, 1);

    const itemReorder: AuthPerm = { id: movedItem.id };

    // update server
    const request: RequestPermissionSet = {
      data: [itemReorder]
      , processFilter: (direction === 'up') ? 1 : 2
    };

    this.authService.permissionUpdate$(request)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => {
          this.loading = false;

          siblings.splice(newIndex, 0, movedItem);
          this.updateSelectedItemAndKeys(movedItem);
        }))
      .subscribe(
        (response) => {

        }
      );

  }

  protected updateSelectedItemAndKeys(item: TreeDataItem): void {

    this.selectedItem = item;

    if (item.id) {
      this.selectedKeys = [item.id];
    } else {
      this.selectedKeys = [];
    }
  }

  //---------------- lookups -----------//

  protected findIndexInTree(tree: TreeDataItem[] | undefined | null, itemId: string): number {
    if (tree) {
      for (let i = 0; i < tree.length; i++) {
        if (tree[i].id === itemId) {
          return i; // Item found at the current level
        }

        if (tree[i].items) {
          const indexInChildren = this.findIndexInTree(tree[i].items, itemId);
          if (indexInChildren !== -1) {
            return indexInChildren; // Item found in children
          }
        }
      }

      return -1; // Item not found in the current level or its children
    }
    return -2
  }

  protected findParent(data: TreeDataItem[], itemId: string): TreeDataItem | undefined {
    for (const item of data) {
      if (item.items && item.items.some(child => child.id === itemId)) {
        return item;
      }
      if (item.items) {
        const found = this.findParent(item.items, itemId);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  protected findItemInTree(data: TreeDataItem[], itemId: string): TreeDataItem | undefined {
    for (const item of data) {
      if (item.id === itemId) {
        return item;
      }
      if (item.items) {
        const found = this.findItemInTree(item.items, itemId);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  //----------------Make the State 'none-intermediate-checked'-----------//

  public isChecked = (dataItem: TreeDataItem, index: string): CheckedState => {

    // Check that there are no children
    if (!Array.isArray(dataItem.items) || !dataItem.items.length) {

      if (this.containsCheckedItem(dataItem))
        return "checked";
      else
        return "none";
    }
    else {
      // has children
      let v = this.hasCheckedChildren(dataItem.items);
      if (v === 1) {
        return "checked";
      }
      else if (v === -1) {
        return "indeterminate";
      }
      else
        return "none";
    }

    return "none";
  }

  // return 1 for all children checked, 0 for none , -1 for some
  protected hasCheckedChildren(items: TreeDataItem[] | undefined | null): number {

    if (!Array.isArray(items) || !items.length) {
      return 0;
    }

    let countChecked: number = 0;
    let countIndeterminate: number = 0;

    for (let idx = 0; idx < items.length; idx++) {

      let item = items[idx];

      if ((!Array.isArray(item.items) || !item.items.length)) {
        // has no children
        if (this.containsCheckedItem(item))
          countChecked++;
      }
      else {
        let childState = this.hasCheckedChildren(item.items);
        if (childState === 1) {
          countChecked++;
        }
        else if (childState === -1) {
          countIndeterminate++;
        }
      }

    }

    if (countChecked > 0) {
      if (countChecked < items.length)
        return -1;
      else if (countChecked == items.length)
        return 1;
    }
    else if (countIndeterminate > 0)
      return -1;

    return 0;
  }

  protected containsCheckedItem(item: any): boolean {
    return this.checkedKeys.indexOf(item[this.key]) > -1;
  }

  // --------------------------------------------------------------------------

  protected setCssClass(items: TreeDataItem[] | undefined | null) {

    if (!Array.isArray(items) || !items.length) {
      return;
    }

    items.forEach(x => {
      if (x.dataType && x.dataType.length > 0) {
        x.spriteCssClass += " isg-" + x.dataType;
      }

      this.setCssClass(x.items);
    });

  }

  protected getCssClass(item: TreeDataItem) {

    let ci: string = "";

    if (item.isLocked) {
      ci = " isg-locked";
    }

    if (this.excludedIds.includes(item.id!, 0)) {
      ci += " isg-exclude";
    }
    else if (this.customIds.includes(item.id!, 0)) {
      ci += " isg-user-custom";
    }

    if (this.hasChildExcludedIds.includes(item.id!, 0)) {
      ci += " isg-exclude-child";
    }

    if (this.hasChildCustomIds.includes(item.id!, 0)) {
      ci += " isg-user-custom-child";
    }

    ci = item.spriteCssClass + ci;

    return ci;
  }
}
